/**
 * PRE-143: Datadog deep-link builders for the admin panel (option A).
 *
 * The worker and the SPA share these so a Costs row and a Health card open the
 * same Explorer query. There is no official per-span permalink for LLM Obs —
 * the Explorer `query=@span_id:{id}` workaround is what Datadog documents.
 * Time windows are unix-ms `from_ts` / `to_ts` so Explorer's default ~1 week
 * range does not hide an older call.
 */
export const DATADOG_APP_ORIGIN = 'https://app.datadoghq.com';
export const DATADOG_SERVICE = 'prepzio-api';
export const DATADOG_ML_APP = 'prepzio';

const HOUR_MS = 3_600_000;
const DAY_MS = 24 * HOUR_MS;

function search(params: Record<string, string | number | undefined>): string {
  const u = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === '') continue;
    u.set(key, String(value));
  }
  return u.toString();
}

/** `llm_calls.created_at` is UTC `YYYY-MM-DD HH:MM:SS` with no timezone suffix. */
export function ledgerTimeMs(createdAt: string | Date): number {
  if (createdAt instanceof Date) {
    const t = createdAt.getTime();
    return Number.isFinite(t) ? t : Date.now();
  }
  const trimmed = createdAt.trim();
  const iso = /[zZ]|[+-]\d{2}:\d{2}$/.test(trimmed) ? trimmed : `${trimmed.replace(' ', 'T')}Z`;
  const t = new Date(iso).getTime();
  return Number.isFinite(t) ? t : Date.now();
}

function windowAround(
  createdAt: string | Date | undefined,
  now = Date.now(),
): {
  from_ts: number;
  to_ts: number;
} {
  if (createdAt === undefined) {
    return { from_ts: now - 6 * HOUR_MS, to_ts: now + HOUR_MS };
  }
  const t = ledgerTimeMs(createdAt);
  return { from_ts: t - 6 * HOUR_MS, to_ts: t + HOUR_MS };
}

/** LLM Observability Trace Explorer filtered to one span. */
export function datadogLlmSpanUrl(spanId: string, createdAt?: string | Date): string {
  const win = windowAround(createdAt);
  return `${DATADOG_APP_ORIGIN}/llm/traces?${search({
    query: `@span_id:${spanId}`,
    from_ts: win.from_ts,
    to_ts: win.to_ts,
  })}`;
}

/** LLM Observability Trace Explorer filtered to one learner. */
export function datadogLlmUserUrl(userId: string): string {
  return `${DATADOG_APP_ORIGIN}/llm/traces?${search({
    query: `@user_id:${userId} @ml_app:${DATADOG_ML_APP}`,
  })}`;
}

export type DatadogSystemHealthLinks = {
  apmTraces: string;
  errorTracking: string;
  llmObs: string;
  apmService: string;
};

/** Prefilled Datadog links for the System health screen (last 24h). */
export function datadogSystemHealthLinks(env: string, now = Date.now()): DatadogSystemHealthLinks {
  const fromTs = now - DAY_MS;
  const toTs = now;
  return {
    apmTraces: `${DATADOG_APP_ORIGIN}/apm/traces?${search({
      query: `service:${DATADOG_SERVICE} env:${env}`,
      from_ts: fromTs,
      to_ts: toTs,
    })}`,
    errorTracking: `${DATADOG_APP_ORIGIN}/apm/error-tracking?${search({
      query: `service:${DATADOG_SERVICE} env:${env}`,
    })}`,
    llmObs: `${DATADOG_APP_ORIGIN}/llm/traces?${search({
      query: `@ml_app:${DATADOG_ML_APP} env:${env}`,
      from_ts: fromTs,
      to_ts: toTs,
    })}`,
    apmService: `${DATADOG_APP_ORIGIN}/apm/services/${encodeURIComponent(DATADOG_SERVICE)}?${search(
      {
        env,
      },
    )}`,
  };
}
